import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Folder, FolderPlus, Image, FileText, File, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { useToast } from '../hooks/use-toast';

const DirectoryList = ({ directories, user, onCreateDirectory, onDeleteDirectory, onDirectoryClick, files }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newDirectoryName, setNewDirectoryName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const isAdmin = user && (user.is_admin === 1 || user.is_admin === true);

  const getDirectoryIcon = (type) => {
    // ... same ...
    switch (type) {
      case 'image':
        return <Image className="h-5 w-5" />;
      case 'file-text':
        return <FileText className="h-5 w-5" />;
      case 'file':
        return <File className="h-5 w-5" />;
      default:
        return <Folder className="h-5 w-5" />;
    }
  };

  const getFileCount = (directoryId) => {
    if (directoryId.startsWith('user-')) {
      const targetUsername = directoryId.replace('user-', '');
      return files.filter(f => f.uploaderUsername === targetUsername).length;
    }
    return files.filter(f => f.directoryId === directoryId).length;
  };

  const handleCreateDirectory = () => {
    if (!newDirectoryName.trim()) return;

    const newDirectory = {
      id: `dir-${Date.now()}`,
      name: newDirectoryName,
      type: 'custom',
      icon: 'folder',
      fileCount: 0
    };

    onCreateDirectory(newDirectory);
    setNewDirectoryName('');
    setIsDialogOpen(false);
  };

  const handleDelete = async (directory) => {
    setDeleting(true);
    try {
      const { directoriesApi } = await import('../lib/api');
      await directoriesApi.delete(directory.id);
      onDeleteDirectory(directory.id);
      toast({
        title: 'Directory deleted',
        description: `"${directory.name}" has been deleted.`
      });
    } catch (error) {
      console.error('Delete failed:', error);
      toast({
        title: 'Delete failed',
        description: error.response?.data?.error || 'Failed to delete directory. Ensure it is empty.',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Directories</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white">
              <FolderPlus className="h-4 w-4 mr-2" />
              New Directory
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Directory</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="dir-name" className="text-sm font-medium">
                  Directory Name
                </label>
                <Input
                  id="dir-name"
                  placeholder="Enter directory name"
                  value={newDirectoryName}
                  onChange={(e) => setNewDirectoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateDirectory()}
                />
              </div>
              <Button
                onClick={handleCreateDirectory}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
              >
                Create Directory
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {directories.map((directory) => (
          <div
            key={directory.id}
            onClick={() => onDirectoryClick(directory)}
            className="group relative p-4 border border-gray-200 rounded-lg hover:border-teal-400 hover:shadow-md transition-all duration-200 cursor-pointer bg-white"
          >
            {isAdmin && directory.type !== 'auto' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(directory);
                }}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 transition-opacity p-1 h-auto"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <div className="flex flex-col items-center text-center">
              <div className={`w-16 h-16 rounded-lg flex items-center justify-center mb-3 transition-colors bg-gradient-to-br from-teal-100 to-cyan-100 text-teal-600 group-hover:from-teal-200 group-hover:to-cyan-200`}>
                {getDirectoryIcon(directory.icon)}
              </div>
              <h3 className="font-medium text-gray-900 mb-1">{directory.name}</h3>
              <p className="text-sm text-gray-500">
                {getFileCount(directory.id)} {getFileCount(directory.id) === 1 ? 'file' : 'files'}
              </p>
              {directory.type === 'auto' && (
                <span className="absolute top-2 right-2 text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">
                  Auto
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {deleteConfirm && (
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Directory</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deleteConfirm.name}"? This will only work if the directory is empty.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Card>
  );
};

export default DirectoryList;
